import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Check, Copy, PhoneForwarded } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { selectAgents } from '@/redux/agentsSlice';
import { toast } from '@/hooks/useToast';
import { toLatvianForwardingNumber } from '@/utils/phoneUtils';
import {
  CALL_FORWARDING_OPERATORS,
  FORWARDING_KINDS,
  fillForwardingTemplate,
  type ForwardingCodes,
  type ForwardingKind,
} from '@/data/callForwarding';

const CopyableCode: React.FC<{ value: string }> = ({ value }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => window.clearTimeout(copiedTimeout.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({
        title: t('help.call_forwarding.copied.translation', 'Copied'),
      });
      window.clearTimeout(copiedTimeout.current);
      copiedTimeout.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: t(
          'help.call_forwarding.copy_failed.translation',
          'Could not copy'
        ),
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex max-w-full items-center gap-2 rounded-md bg-muted px-2.5 py-1.5 text-left font-mono text-xs text-foreground transition-colors hover:bg-muted/80 sm:text-sm"
      aria-label={t('help.call_forwarding.copy_code.translation', {
        code: value,
        defaultValue: `Copy ${value}`,
      })}
    >
      <span className="min-w-0 break-all">{value}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
};

const CodeCell: React.FC<{
  spec: ForwardingCodes;
  number: string;
  placeholder: string;
}> = ({ spec, number, placeholder }) => {
  const { t } = useTranslation();

  if (spec.mode === 'none') {
    return (
      <span className="text-sm text-muted-foreground">
        {t('help.call_forwarding.unavailable.translation', '—')}
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {spec.templates.map(template => (
        <CopyableCode
          key={template}
          value={fillForwardingTemplate(template, number, placeholder)}
        />
      ))}
    </div>
  );
};

const CallForwardingGuide: React.FC = () => {
  const { t } = useTranslation();
  const agents = useSelector(selectAgents);
  const [selectedAgentId, setSelectedAgentId] = useState('none');
  const [number, setNumber] = useState('');
  const didAutofill = useRef(false);

  const agentsWithNumbers = useMemo(
    () =>
      agents.filter(
        agent => agent.phone_number || agent.subscription?.sip_number
      ),
    [agents]
  );

  useEffect(() => {
    if (didAutofill.current || agentsWithNumbers.length !== 1) return;
    const agent = agentsWithNumbers[0];
    const resolved = toLatvianForwardingNumber(
      agent.phone_number || agent.subscription?.sip_number
    );
    if (!resolved || agent.id == null) return;
    didAutofill.current = true;
    setSelectedAgentId(String(agent.id));
    setNumber(resolved);
  }, [agentsWithNumbers]);

  const placeholder = t(
    'help.call_forwarding.placeholder.translation',
    'YOUR_NUMBER'
  );

  const handleAgentChange = (value: string) => {
    setSelectedAgentId(value);
    if (value === 'none') return;
    const agent = agentsWithNumbers.find(item => String(item.id) === value);
    const resolved = toLatvianForwardingNumber(
      agent?.phone_number || agent?.subscription?.sip_number
    );
    if (resolved) setNumber(resolved);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PhoneForwarded className="h-5 w-5" />
          {t(
            'help.call_forwarding.title.translation',
            'Call forwarding in Latvia'
          )}
        </CardTitle>
        <CardDescription>
          {t(
            'help.call_forwarding.intro.translation',
            'Call forwarding lets you redirect incoming calls to another Latvian phone number, including your Dialup agent. It is also available for business mobile numbers.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          {t(
            'help.call_forwarding.replace_hint.translation',
            'Codes below use your Dialup number. Select an agent or enter the number you want to forward calls to.'
          )}
        </p>

        <div
          className={
            agentsWithNumbers.length > 0
              ? 'grid grid-cols-1 gap-4 sm:grid-cols-2'
              : 'grid grid-cols-1 gap-4'
          }
        >
          {agentsWithNumbers.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="call-forwarding-agent">
                {t(
                  'help.call_forwarding.select_agent.translation',
                  'Forward to agent'
                )}
              </Label>
              <Select value={selectedAgentId} onValueChange={handleAgentChange}>
                <SelectTrigger id="call-forwarding-agent">
                  <SelectValue
                    placeholder={t(
                      'help.call_forwarding.select_agent_placeholder.translation',
                      'Select an agent (optional)'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t(
                      'help.call_forwarding.select_agent_none.translation',
                      'Enter a number manually'
                    )}
                  </SelectItem>
                  {agentsWithNumbers.map(agent => (
                    <SelectItem key={agent.id} value={String(agent.id)}>
                      {agent.name}
                      {agent.phone_number ? ` · ${agent.phone_number}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="call-forwarding-number">
              {t(
                'help.call_forwarding.number_label.translation',
                'Dialup number'
              )}
            </Label>
            <Input
              id="call-forwarding-number"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={number}
              onChange={event => {
                const next = toLatvianForwardingNumber(event.target.value);
                setNumber(next);
                const selected = agentsWithNumbers.find(
                  agent => String(agent.id) === selectedAgentId
                );
                const selectedNumber = toLatvianForwardingNumber(
                  selected?.phone_number || selected?.subscription?.sip_number
                );
                if (selected && next !== selectedNumber) {
                  setSelectedAgentId('none');
                }
              }}
              placeholder={placeholder}
            />
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {CALL_FORWARDING_OPERATORS.map(operator => (
            <AccordionItem key={operator.id} value={operator.id}>
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="flex flex-wrap items-center gap-2">
                  <span>
                    {t(
                      `help.call_forwarding.operators.${operator.id}.translation`,
                      operator.id.toUpperCase()
                    )}
                  </span>
                  {operator.id === 'lmt' && (
                    <Badge variant="outline" className="font-normal">
                      {t(
                        'help.call_forwarding.method.sms.translation',
                        'SMS to 1688'
                      )}
                    </Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="mb-3 text-sm text-muted-foreground">
                  {operator.id === 'lmt'
                    ? t(
                        'help.call_forwarding.method.sms_detail.translation',
                        'Send an SMS to 1688 with the code for the forwarding type you want.'
                      )
                    : t(
                        'help.call_forwarding.method.dial_detail.translation',
                        'Dial the code, then press the call button.'
                      )}
                </p>
                <div className="space-y-3">
                  {FORWARDING_KINDS.map((kind: ForwardingKind) => (
                    <div
                      key={kind}
                      className="grid grid-cols-1 gap-1.5 sm:grid-cols-[10rem_1fr] sm:items-start"
                    >
                      <span className="pt-1.5 text-sm font-medium text-foreground">
                        {t(
                          `help.call_forwarding.kinds.${kind}.translation`,
                          kind
                        )}
                      </span>
                      <CodeCell
                        spec={operator.codes[kind]}
                        number={number}
                        placeholder={placeholder}
                      />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">
            {t('help.call_forwarding.important.title.translation', 'Important')}
          </h3>
          <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted-foreground">
            <li>
              {t(
                'help.call_forwarding.important.press_call.translation',
                'After entering a dial code, press the call button.'
              )}
            </li>
            <li>
              {t(
                'help.call_forwarding.important.business.translation',
                'Call forwarding on business mobile numbers works the same as on personal numbers.'
              )}
            </li>
            <li>
              {t(
                'help.call_forwarding.important.pbx.translation',
                'If your company uses a virtual PBX, forwarding may need to be set in the PBX itself.'
              )}
            </li>
            <li>
              {t(
                'help.call_forwarding.important.charges.translation',
                'Forwarded-call charges may depend on your operator and plan.'
              )}
            </li>
          </ul>
        </section>
      </CardContent>
    </Card>
  );
};

export default CallForwardingGuide;
